import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { saveMosquitoScore, getMyMosquitoHighScore, getMosquitoLeaderboard } from '../../services/gameApi';
import BackButton from '../../components/BackButton';
import LoginPrompt from '../../components/LoginPrompt';
import './KillMosquito.css';

const DIFFICULTIES = {
  EASY: {
    name: 'Easy',
    gameTime: 45,
    initialSpawnInterval: 2000,
    minSpawnInterval: 800,
    mosquitoSpeedMin: 2000,
    mosquitoSpeedMax: 3500,
    mosquitoLifetime: 4000,
    difficultyIncreaseInterval: 7,
    scoreMultiplier: 1
  },
  MEDIUM: {
    name: 'Medium',
    gameTime: 30,
    initialSpawnInterval: 1500,
    minSpawnInterval: 500,
    mosquitoSpeedMin: 1000,
    mosquitoSpeedMax: 3000,
    mosquitoLifetime: 3000,
    difficultyIncreaseInterval: 5,
    scoreMultiplier: 1.5
  },
  HARD: {
    name: 'Hard',
    gameTime: 20,
    initialSpawnInterval: 1000,
    minSpawnInterval: 300,
    mosquitoSpeedMin: 500,
    mosquitoSpeedMax: 2000,
    mosquitoLifetime: 2500,
    difficultyIncreaseInterval: 3,
    scoreMultiplier: 2
  }
};

export default function KillMosquito() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState('MEDIUM');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DIFFICULTIES.MEDIUM.gameTime);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [mosquitoes, setMosquitoes] = useState([]);
  const [misses, setMisses] = useState(0);
  const [spawnInterval, setSpawnInterval] = useState(DIFFICULTIES.MEDIUM.initialSpawnInterval);
  const [highScore, setHighScore] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const timerRef = useRef(null);
  const spawnTimerRef = useRef(null);
  const gameBoardRef = useRef(null);

  // Load high score from backend (only if authenticated)
  useEffect(() => {
    const loadHighScore = async () => {
      if (!isAuthenticated) {
        // For anonymous users, load from localStorage
        const savedHighScore = localStorage.getItem('mosquitoHighScore');
        if (savedHighScore) {
          setHighScore(parseInt(savedHighScore, 10));
        }
        return;
      }
      
      try {
        const response = await getMyMosquitoHighScore();
        if (response.data && response.data.highScore) {
          setHighScore(response.data.highScore);
        }
      } catch (error) {
        console.error('Failed to load high score:', error);
        // Fallback to localStorage
        const savedHighScore = localStorage.getItem('mosquitoHighScore');
        if (savedHighScore) {
          setHighScore(parseInt(savedHighScore, 10));
        }
      }
    };
    loadHighScore();
  }, [isAuthenticated]);

  // Game timer
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (timeLeft === 0) {
      setGameOver(true);
      // Save score to backend
      saveScoreToBackend();
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('mosquitoHighScore', score.toString());
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft, gameStarted, gameOver, score, highScore]);

  // Spawn mosquitoes
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const config = DIFFICULTIES[difficulty];

    const spawnMosquito = () => {
      const id = Date.now() + Math.random();
      const top = Math.random() * 80 + 5; // 5% to 85%
      const left = Math.random() * 80 + 5; // 5% to 85%
      
      setMosquitoes((prev) => [
        ...prev,
        {
          id,
          top,
          left,
          speed: Math.random() * (config.mosquitoSpeedMax - config.mosquitoSpeedMin) + config.mosquitoSpeedMin,
        },
      ]);

      // Remove mosquito after lifetime if not killed (don't count as miss - it's just expired)
      setTimeout(() => {
        setMosquitoes((prev) => prev.filter((m) => m.id !== id));
        // Don't count expired mosquitoes as misses - only count actual click misses
      }, config.mosquitoLifetime);
    };

    spawnMosquito();
    spawnTimerRef.current = setInterval(spawnMosquito, spawnInterval);

    return () => {
      if (spawnTimerRef.current) {
        clearInterval(spawnTimerRef.current);
      }
    };
  }, [gameStarted, gameOver, spawnInterval, difficulty]);

  // Increase difficulty as score increases
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const config = DIFFICULTIES[difficulty];
    if (score > 0 && score % config.difficultyIncreaseInterval === 0) {
      setSpawnInterval((prev) => Math.max(config.minSpawnInterval, prev - 50));
    }
  }, [score, gameStarted, gameOver, difficulty]);

  const handleKill = useCallback((id) => {
    if (gameOver) return;
    
    setScore((prev) => prev + 1);
    setMosquitoes((prev) => prev.filter((m) => m.id !== id));
  }, [gameOver]);

  const handleMiss = useCallback(() => {
    if (gameOver) return;
    setMisses((prev) => prev + 1);
  }, [gameOver]);

  const startGame = (selectedDifficulty = difficulty) => {
    const config = DIFFICULTIES[selectedDifficulty];
    setDifficulty(selectedDifficulty);
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setTimeLeft(config.gameTime);
    setMisses(0);
    setMosquitoes([]);
    setSpawnInterval(config.initialSpawnInterval);
  };

  const saveScoreToBackend = async () => {
    try {
      if (isAuthenticated) {
        // Save to backend if authenticated
        await saveMosquitoScore(score, misses);
      } else {
        // For anonymous users, save to localStorage for later sync
        const pendingScore = {
          score,
          misses,
          timestamp: Date.now()
        };
        localStorage.setItem('pendingMosquitoScore', JSON.stringify(pendingScore));
        console.log('Score saved locally. Login to sync with server.');
      }
    } catch (error) {
      console.error('Failed to save score:', error);
      // Fallback to localStorage if backend save fails
      if (isAuthenticated) {
        const pendingScore = {
          score,
          misses,
          timestamp: Date.now()
        };
        localStorage.setItem('pendingMosquitoScore', JSON.stringify(pendingScore));
      }
    }
  };

  const loadLeaderboard = async () => {
    if (!isAuthenticated) {
      // Show login prompt instead of loading leaderboard
      setShowLoginPrompt(true);
      return;
    }
    
    setLoading(true);
    try {
      const response = await getMosquitoLeaderboard();
      setLeaderboard(response.data || []);
      setShowLeaderboard(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setShowLoginPrompt(true);
      } else {
        console.error('Failed to load leaderboard:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetGame = () => {
    const config = DIFFICULTIES[difficulty];
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setTimeLeft(config.gameTime);
    setMisses(0);
    setMosquitoes([]);
    setSpawnInterval(config.initialSpawnInterval);
    setShowLeaderboard(false);
    if (timerRef.current) clearInterval(timerRef.current);
    if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
  };

  return (
    <div className="kill-mosquito-container">
      <BackButton />
      <div className="game-header">
        <h1>🦟 Kill the Mosquito</h1>
        <div className="game-stats">
          <div className="stat">
            <span className="stat-label">Score:</span>
            <span className="stat-value">{score}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Time:</span>
            <span className="stat-value">{timeLeft}s</span>
          </div>
          <div className="stat">
            <span className="stat-label">Misses:</span>
            <span className="stat-value">{misses}</span>
          </div>
          <div className="stat">
            <span className="stat-label">High Score:</span>
            <span className="stat-value">{highScore}</span>
          </div>
          {gameStarted && (
            <div className="stat">
              <span className="stat-label">Difficulty:</span>
              <span className="stat-value">{DIFFICULTIES[difficulty].name}</span>
            </div>
          )}
        </div>
      </div>

      {!gameStarted && !gameOver && !showLeaderboard && (
        <div className="game-start-screen">
          <h2>Ready to Kill Some Mosquitoes?</h2>
          <p>Click on mosquitoes as fast as you can!</p>
          
          <div className="difficulty-selection">
            <h3>Select Difficulty:</h3>
            <div className="difficulty-buttons">
              <button
                className={`difficulty-btn ${difficulty === 'EASY' ? 'active' : ''}`}
                onClick={() => setDifficulty('EASY')}
              >
                <span className="difficulty-icon">😊</span>
                <span className="difficulty-name">Easy</span>
                <span className="difficulty-desc">45s • Slow • Relaxed</span>
              </button>
              <button
                className={`difficulty-btn ${difficulty === 'MEDIUM' ? 'active' : ''}`}
                onClick={() => setDifficulty('MEDIUM')}
              >
                <span className="difficulty-icon">😐</span>
                <span className="difficulty-name">Medium</span>
                <span className="difficulty-desc">30s • Normal • Balanced</span>
              </button>
              <button
                className={`difficulty-btn ${difficulty === 'HARD' ? 'active' : ''}`}
                onClick={() => setDifficulty('HARD')}
              >
                <span className="difficulty-icon">😤</span>
                <span className="difficulty-name">Hard</span>
                <span className="difficulty-desc">20s • Fast • Intense</span>
              </button>
            </div>
          </div>

          <div className="game-buttons">
            <button className="start-button" onClick={() => startGame(difficulty)}>
              Start Game ({DIFFICULTIES[difficulty].name})
            </button>
            <button 
              className={`leaderboard-button ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={loadLeaderboard}
              disabled={!isAuthenticated}
              title={!isAuthenticated ? 'Login to view leaderboard' : 'View leaderboard'}
            >
              {isAuthenticated ? 'View Leaderboard' : 'Login to View Leaderboard'}
            </button>
          </div>
        </div>
      )}

      {showLoginPrompt && (
        <LoginPrompt
          feature="games"
          subFeature="leaderboard"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      {showLeaderboard && (
        <div className="leaderboard-screen">
          <h2>🏆 Leaderboard</h2>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="leaderboard-list">
                {leaderboard.length === 0 ? (
                  <p>No scores yet. Be the first!</p>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="username">{entry.username}</span>
                      <span className="score">{entry.score}</span>
                      <span className="accuracy">{entry.accuracy.toFixed(2)}%</span>
                    </div>
                  ))
                )}
              </div>
              <button className="reset-button" onClick={() => setShowLeaderboard(false)}>
                Back
              </button>
            </>
          )}
        </div>
      )}

      {gameOver && (
        <div className="game-over-screen">
          <h2>Game Over!</h2>
          <div className="final-stats">
            <p>Final Score: <strong>{score}</strong></p>
            <p>Misses: <strong>{misses}</strong></p>
            <p>Accuracy: <strong>{score + misses > 0 ? ((score / (score + misses)) * 100).toFixed(2) : 0}%</strong></p>
            {score === highScore && <p className="new-high-score">🎉 New High Score!</p>}
          </div>
          <div className="game-buttons">
            <button className="start-button" onClick={() => startGame(difficulty)}>
              Play Again ({DIFFICULTIES[difficulty].name})
            </button>
            <button 
              className={`leaderboard-button ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={loadLeaderboard}
              disabled={!isAuthenticated}
              title={!isAuthenticated ? 'Login to view leaderboard' : 'View leaderboard'}
            >
              {isAuthenticated ? 'View Leaderboard' : 'Login to View Leaderboard'}
            </button>
            <button className="reset-button" onClick={resetGame}>
              Main Menu
            </button>
          </div>
        </div>
      )}

      {gameStarted && !gameOver && (
        <div
          ref={gameBoardRef}
          className="game-board"
          onClick={handleMiss}
        >
          {mosquitoes.map((mosquito) => (
            <Mosquito
              key={mosquito.id}
              id={mosquito.id}
              top={mosquito.top}
              left={mosquito.left}
              speed={mosquito.speed}
              onKill={handleKill}
            />
          ))}
          {mosquitoes.length === 0 && (
            <div className="waiting-message">Mosquitoes are coming...</div>
          )}
        </div>
      )}
    </div>
  );
}

function Mosquito({ id, top, left, speed, onKill }) {
  const [position, setPosition] = useState({ top, left });
  const [isKilled, setIsKilled] = useState(false);
  const moveTimerRef = useRef(null);

  useEffect(() => {
    if (isKilled) return;

    moveTimerRef.current = setInterval(() => {
      const newTop = Math.random() * 80 + 5;
      const newLeft = Math.random() * 80 + 5;
      setPosition({ top: newTop, left: newLeft });
    }, speed);

    return () => {
      if (moveTimerRef.current) {
        clearInterval(moveTimerRef.current);
      }
    };
  }, [speed, isKilled]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (isKilled) return;
    
    setIsKilled(true);
    setTimeout(() => {
      onKill(id);
    }, 200);
  };

  return (
    <>
      {isKilled && (
        <div
          className="splat"
          style={{
            top: `${position.top}%`,
            left: `${position.left}%`,
          }}
        >
          💥
        </div>
      )}
      {!isKilled && (
        <div
          className="mosquito"
          style={{
            top: `${position.top}%`,
            left: `${position.left}%`,
          }}
          onClick={handleClick}
        >
          🦟
        </div>
      )}
    </>
  );
}

